import { AntDesign, MaterialIcons } from "@expo/vector-icons";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import _ from "lodash";
// eslint-disable-next-line import/no-named-default
import { default as MomentLib } from "moment";
import { extendMoment } from "moment-range";
import {
  AlertDialog,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Modal,
  Pressable,
  ScrollView,
  Spinner,
  Text,
  useDisclose,
  useTheme,
  VStack,
} from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CheckBoxCom from "../components/checkBox";
import CustomBrandingMobileScreenHeader from "../components/CustomBrandingMobileScreenHeader";
import GroceryListDatePicker from "../components/GroceryListDatePicker";
import ScreenContainer from "../components/ScreenContainer";
import { commonStyles, isDesktopScreen, Routes, Scale } from "../constants";
import { formatMomentAsDateForApi } from "../helpers/apiHelpers";
import { getGroceryListItemInWeight, shouldWeUseImperialForThisUser } from "../helpers/foodHelpers";
import { formatDate } from "../helpers/generalHelpers";
import { isSimpleOrg } from "../helpers/userHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type {
  Category3C2Enum as CategoryEnum,
  GroceryListItemUpdate as GroceryListItem,
  GroceryListListRetrieve,
  SimpleGroceryList,
  SimpleGroceryListItem,
} from "../services/backendTypes";
// Removed plannerSlice import - no longer needed
import { userSelector } from "../slices/userSlice";

const {
  usePlannerGroceryListCreateMutation,
  usePlannerGroceryListDestroyMutation,
  usePlannerGroceryListItemPartialUpdateMutation,
  usePlannerGroceryListListQuery,
  // Add simple grocery list hooks
  usePlannerSimpleGroceryListCreateMutation,
  usePlannerSimpleGroceryListDestroyMutation,
  usePlannerSimpleGroceryListItemPartialUpdateMutation,
  usePlannerSimpleGroceryListListQuery,
} = backendApi;

// NOTE: This is an internal type error with MomentLib
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const moment = extendMoment(MomentLib);

// Helper function to format category names for simple organizations
const formatCategoryName = (category: string): string => {
  // Handle empty or undefined categories
  if (!category) return "Other";

  // Handle special cases
  if (category === "OTHER") return "Other";

  // Check if category is already properly formatted (contains lowercase letters)
  // This preserves categories like "Vegetables" or "Meat and Fish" from LLM
  if (/[a-z]/.test(category) && !category.includes("_")) {
    // Just ensure first letter is capitalized
    return category.charAt(0).toUpperCase() + category.slice(1);
  }

  // For all-caps categories with underscores (e.g., "MEAT_AND_FISH")
  return category
    .split("_")
    .map((word) => {
      // Special handling for "AND" - replace with ampersand for better readability
      if (word.toUpperCase() === "AND") return "&";

      // Title case each word - capitalize first letter, lowercase the rest
      if (word.length === 0) return "";
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ")
    .replace(/\s+/g, " ") // Remove any extra spaces
    .trim();
};

type Props = NativeStackScreenProps<RootStackParamList, Routes.GroceryListScreen>;

const GroceryListScreen = (props: Props): JSX.Element => {
  const { t } = useTranslation();

  // TODO: This functionality is not currently used but when it is this hook should be called safely
  // const user = useSelector(viewAsUserSelector) || useSelector(userSelector);
  const user = useSelector(userSelector);

  const isDesktop = isDesktopScreen();

  const { onOpen, isOpen, onClose } = useDisclose();
  const cancelRef = React.useRef(null);

  const theme = useTheme();

  const {
    isOpen: isOpenGroceryListDatePicker,
    onOpen: onOpenGroceryListDatePicker,
    onClose: onCloseGroceryListDatePicker,
  } = useDisclose();

  if (!user) {
    throw new Error("User is not set");
  }

  const isSimpleOrganization = isSimpleOrg(user);

  // Always call all hooks, then choose which to use
  const [updateGroceryListItemOnBackendSimple, { isLoading: isLoadingUpdateGroceryListItemSimple }] =
    usePlannerSimpleGroceryListItemPartialUpdateMutation();
  const [updateGroceryListItemOnBackendComplex, { isLoading: isLoadingUpdateGroceryListItemComplex }] =
    usePlannerGroceryListItemPartialUpdateMutation();

  const simpleGroceryListQuery = usePlannerSimpleGroceryListListQuery({
    // Optional: Add date filtering if needed
    // startDate: "2025-01-01",
    // endDate: "2025-01-31"
  });
  const complexGroceryListQuery = usePlannerGroceryListListQuery({ user: user.id });

  // Select the appropriate hooks based on organization type
  const updateGroceryListItemOnBackend = isSimpleOrganization
    ? updateGroceryListItemOnBackendSimple
    : updateGroceryListItemOnBackendComplex;
  const isLoadingUpdateGroceryListItem = isSimpleOrganization
    ? isLoadingUpdateGroceryListItemSimple
    : isLoadingUpdateGroceryListItemComplex;
  const {
    data: groceryListsDataFromApi,
    refetch: refetchGroceryLists,
    isLoading: isLoadingGroceryLists,
  } = isSimpleOrganization ? simpleGroceryListQuery : complexGroceryListQuery;

  // Get grocery lists directly from RTK Query
  const groceryLists = groceryListsDataFromApi?.results ?? [];

  // NOTE: We assume the user only ever has 1 grocery list at a time and we always fetch the last created
  const groceryList = _.chain(groceryLists).sortBy("id").reverse().head().value() as
    | GroceryListListRetrieve
    | SimpleGroceryList
    | undefined;

  // Removed useEffect - now using RTK Query data directly

  const toggleCheckedStatusOnGroceryListItem = async (item: GroceryListItem | SimpleGroceryListItem): Promise<void> => {
    if (!item.id) {
      throw new Error("Grocery list item id is not defined");
    }

    if (isSimpleOrganization) {
      await updateGroceryListItemOnBackendSimple({
        id: item.id,
        patchedSimpleGroceryListItemRequest: {
          is_checked: !("is_checked" in item ? item.is_checked : (item as GroceryListItem).checked),
        },
      });
    } else {
      await updateGroceryListItemOnBackendComplex({
        id: item.id,
        patchedGroceryListItemUpdateRequest: {
          checked: !(item as GroceryListItem).checked,
        },
      });
    }
  };

  const onPressGroceryListItem = async (item: GroceryListItem | SimpleGroceryListItem): Promise<void> => {
    await toggleCheckedStatusOnGroceryListItem(item);
    refetchGroceryLists();
  };

  // Always call all hooks, then choose which to use
  const [createGroceryListOnBackendSimple, { isLoading: isLoadingCreateGroceryListOnBackendSimple }] =
    usePlannerSimpleGroceryListCreateMutation();
  const [createGroceryListOnBackendComplex, { isLoading: isLoadingCreateGroceryListOnBackendComplex }] =
    usePlannerGroceryListCreateMutation();
  const [deleteGroceryListOnBackendSimple, { isLoading: isLoadingDeleteGroceryListSimple }] =
    usePlannerSimpleGroceryListDestroyMutation();
  const [deleteGroceryListOnBackendComplex, { isLoading: isLoadingDeleteGroceryListComplex }] =
    usePlannerGroceryListDestroyMutation();

  // Select the appropriate hooks based on organization type
  const createGroceryListOnBackend = isSimpleOrganization
    ? createGroceryListOnBackendSimple
    : createGroceryListOnBackendComplex;
  const isLoadingCreateGroceryListOnBackend = isSimpleOrganization
    ? isLoadingCreateGroceryListOnBackendSimple
    : isLoadingCreateGroceryListOnBackendComplex;
  const deleteGroceryListOnBackend = isSimpleOrganization
    ? deleteGroceryListOnBackendSimple
    : deleteGroceryListOnBackendComplex;
  const isLoadingDeleteGroceryList = isSimpleOrganization
    ? isLoadingDeleteGroceryListSimple
    : isLoadingDeleteGroceryListComplex;

  const onPressCreateGroceryListButton = (): void => onOpenGroceryListDatePicker();

  const createGroceryList = async (startDate: moment.Moment, endDate: moment.Moment): Promise<void> => {
    if (isSimpleOrganization) {
      await createGroceryListOnBackendSimple({
        simpleGroceryListCreateUpdateRequest: {
          start_date: formatMomentAsDateForApi(startDate),
          end_date: formatMomentAsDateForApi(endDate),
        },
      });
    } else {
      await createGroceryListOnBackendComplex({
        groceryListUpdateCreateRequest: {
          start_date: formatMomentAsDateForApi(startDate),
          end_date: formatMomentAsDateForApi(endDate),
          user: user.id,
        },
      });
    }

    onCloseGroceryListDatePicker();
    refetchGroceryLists();
  };

  const deleteGroceryList = async (): Promise<void> => {
    onClose();

    if (!groceryList?.id) {
      return;
    }

    if (isSimpleOrganization) {
      await deleteGroceryListOnBackendSimple({
        id: groceryList.id,
      });
      return;
    }
    await deleteGroceryListOnBackendComplex({
      id: groceryList.id,
    });
    refetchGroceryLists();
  };
  const shouldUseImperial = shouldWeUseImperialForThisUser(user);

  const createGroceryListItemComponent = (item: GroceryListItem | SimpleGroceryListItem): JSX.Element => {
    const isChecked = isSimpleOrganization
      ? (item as SimpleGroceryListItem).is_checked
      : (item as GroceryListItem).checked;
    const itemText = isSimpleOrganization
      ? (item as SimpleGroceryListItem).text
      : `${(item as GroceryListItem).name} – ${getGroceryListItemInWeight(item as GroceryListItem, shouldUseImperial)}`;

    return (
      <Pressable
        key={item.id}
        onPress={async () => onPressGroceryListItem(item)}
        testID={`grocery-list-item-${isChecked ? "checked" : "unchecked"}-${item.id}`}
        _pressed={{
          bg: "gray.50",
        }}
      >
        <HStack space={3} alignItems="center" py={0.5} px={0}>
          <Box
            size={4}
            borderWidth={1}
            borderColor="function.30"
            bg={isChecked ? "function.40" : "transparent"}
            alignItems="center"
            justifyContent="center"
            borderRadius={2}
          >
            {isChecked ? <Icon as={AntDesign} name="check" size={2} color="function.10" /> : null}
          </Box>
          <Text
            flex={1}
            fontSize="sm"
            color={isChecked ? "function.30" : "function.40"}
            strikeThrough={isChecked}
            fontFamily={theme.fonts.body}
          >
            {itemText}
          </Text>
        </HStack>
      </Pressable>
    );
  };

  const allGroceryItems = (groceryList?.items || []) as Array<GroceryListItem | SimpleGroceryListItem>;
  const groupedAllGroceryItems = _.groupBy(allGroceryItems, (groceryListItem): string => {
    if (isSimpleOrganization) {
      return (groceryListItem as SimpleGroceryListItem).category || "OTHER";
    }
    return (groceryListItem as GroceryListItem)?.category || "OTHER";
  });

  const groceryItems = (
    <VStack space={4}>
      {Object.keys(groupedAllGroceryItems).map((category) => (
        <Box key={category}>
          <Text
            fontSize="md"
            fontWeight="bold"
            color="function.40"
            textTransform={isSimpleOrganization ? "none" : "uppercase"}
            fontFamily={theme.fonts.body}
            mt={category !== Object.keys(groupedAllGroceryItems)[0] ? 4 : 0}
            mb={2}
          >
            {isSimpleOrganization
              ? formatCategoryName(category)
              : t(
                  `general.grocery_categories.${
                    category as CategoryEnum
                  }` as `general.grocery_categories.${CategoryEnum}`
                ) || category}
          </Text>
          <VStack space={0.5} pl={3}>
            {(groupedAllGroceryItems[category] || []).map((item) => (
              <Box key={item.id}>{createGroceryListItemComponent(item)}</Box>
            ))}
          </VStack>
        </Box>
      ))}
    </VStack>
  );

  const datePickerModal = (
    <Modal isOpen={isOpenGroceryListDatePicker} onClose={onCloseGroceryListDatePicker} size="xl">
      <Modal.Content bg="primary.10" borderRadius={theme.borderRadii.modal.default}>
        <Modal.CloseButton />
        <Modal.Header bg="primary.10" borderBottomWidth={0} pb={0} pt={12}>
          <Text fontFamily={theme.fonts.serif} fontSize="2xl" color="function.60" textAlign="center" fontStyle="italic">
            {t("grocery_list.select_date_range_label_text")}
          </Text>
        </Modal.Header>
        <Modal.Body pt={-8}>
          <GroceryListDatePicker
            createGroceryList={createGroceryList}
            isCreating={isLoadingCreateGroceryListOnBackend}
          />
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );

  const createGroceryListButtonComponent = (
    <Box mt={8} mb={4}>
      <Button
        disabled={isLoadingCreateGroceryListOnBackend}
        onPress={onPressCreateGroceryListButton}
        testID={"createGroceryList-button"}
        size="lg"
        colorScheme="primary"
        borderRadius={theme.borderRadii.button.default}
        leftIcon={<Icon as={MaterialIcons} name="add-shopping-cart" size="sm" />}
        isLoading={isLoadingCreateGroceryListOnBackend}
        _pressed={{ bg: "primary.700" }}
      >
        {t("grocery_list.create_new_grocery_list_button_text")}
      </Button>
    </Box>
  );

  const listHeaderComponent = (
    <HStack justifyContent="space-between" alignItems="center" mb={0}>
      <Text fontSize="3xl" fontWeight="normal" color="function.50" fontFamily={theme.fonts.serif} fontStyle="italic">
        {groceryList?.start_date ? formatDate(moment(groceryList.start_date)) : null}
        {groceryList?.start_date && groceryList?.end_date ? " — " : null}
        {groceryList?.end_date ? formatDate(moment(groceryList.end_date)) : null}
      </Text>

      {isLoadingDeleteGroceryList ? (
        <Spinner color="primary.50" />
      ) : (
        <IconButton
          icon={<Icon as={MaterialIcons} name="delete" size="lg" color="primary.50" />}
          onPress={onOpen}
          testID={"deleteGroceryList-button"}
          variant="ghost"
          borderRadius="full"
          _pressed={{ bg: "primary.20" }}
          size="lg"
        />
      )}
    </HStack>
  );

  const listOfGroceriesComponent = (
    <VStack space={3}>
      {listHeaderComponent}

      {allGroceryItems.length === 0 ? (
        <Center py={20}>
          <VStack space={6} alignItems="center">
            <Box bg="function.10" p={6} borderRadius="full" borderWidth={2} borderColor="function.20">
              <Text fontSize="4xl" fontFamily={theme.fonts.body}>
                🛒
              </Text>
            </Box>
            <VStack space={2} alignItems="center">
              <Text
                fontSize="xl"
                fontWeight="bold"
                color="function.50"
                textAlign="center"
                fontFamily={theme.fonts.heading}
              >
                {t("grocery_list.this_grocery_list_is_empty_label_text")}
              </Text>
              <Text fontSize="md" color="function.30" textAlign="center" fontFamily={theme.fonts.body}>
                {t("grocery_list.this_grocery_list_is_empty_label_text")}
              </Text>
            </VStack>
          </VStack>
        </Center>
      ) : (
        <VStack space={6}>{groceryItems}</VStack>
      )}
    </VStack>
  );

  const pageHeader = (
    <Box style={commonStyles.paddingContainer} pb={4}>
      <Text
        fontSize="2xl"
        fontWeight="bold"
        color="function.50"
        fontFamily={theme.fonts.heading}
        style={{ paddingVertical: Scale(15) }}
      >
        {t("bottom_tabs.groceries_tab_name")}
      </Text>
    </Box>
  );

  const groceryListComponent = (
    <ScrollView showsVerticalScrollIndicator={false} flex={1}>
      <Box minH="full">
        {/* Organization Logo at top */}
        <Box px={5} pt={4} pb={6}>
          <CustomBrandingMobileScreenHeader />
        </Box>

        <Box px={5} pb={8}>
          {(() => {
            if (_.isEmpty(groceryLists)) {
              return (
                <Center flex={1} py={20}>
                  <VStack alignItems="center" maxW="320px">
                    {/* Icon */}
                    <Box bg="primary.50" p={4} borderRadius="full" shadow={2} mb={4}>
                      <Icon as={MaterialIcons} name="receipt" size="sm" color="primary.10" />
                    </Box>

                    {/* Subtitle only */}
                    <VStack alignItems="center" mb={16}>
                      <Text
                        fontSize="md"
                        color="function.50"
                        textAlign="center"
                        lineHeight="md"
                        fontFamily={theme.fonts.body}
                      >
                        {t("grocery_list.empty_state_message")}
                      </Text>
                    </VStack>

                    {/* Create Button */}
                    <Button
                      onPress={onPressCreateGroceryListButton}
                      testID="createGroceryList-button"
                      size="lg"
                      bg="primary.50"
                      _text={{
                        color: "white",
                        fontWeight: "medium",
                        fontSize: "md",
                      }}
                      _pressed={{
                        bg: "primary.50",
                      }}
                      borderRadius={theme.borderRadii.button.default}
                      px={8}
                      py={2}
                    >
                      {t("grocery_list.create_new_grocery_list_button_text")}
                    </Button>
                  </VStack>
                </Center>
              );
            }
            return listOfGroceriesComponent;
          })()}
        </Box>
      </Box>
    </ScrollView>
  );

  const deleteConfirmationDialog = (
    <AlertDialog leastDestructiveRef={cancelRef} isOpen={isOpen}>
      <AlertDialog.Content bg="primary.10" borderRadius={theme.borderRadii.modal.default}>
        <AlertDialog.Body bg="primary.10" p={6}>
          <VStack space={4} alignItems="center">
            <Icon as={MaterialIcons} name="warning" size="2xl" color="warning.500" />
            <Text
              fontSize="lg"
              fontWeight="medium"
              textAlign="center"
              color="function.50"
              fontFamily={theme.fonts.body}
            >
              {t("general.dialog_confirmation_question")}
            </Text>
          </VStack>
        </AlertDialog.Body>
        <AlertDialog.Footer bg="primary.10" borderTopWidth={0}>
          <HStack space={3} flex={1}>
            <Button
              bg="white"
              borderWidth={1}
              borderColor="function.20"
              _text={{ color: "function.30" }}
              _pressed={{ bg: "function.10" }}
              flex={1}
              onPress={onClose}
              borderRadius={theme.borderRadii.button.default}
              shadow="1"
            >
              {t("general.cancel")}
            </Button>
            <Button
              bg="error.500"
              _text={{ color: "white" }}
              _pressed={{ bg: "error.600" }}
              shadow="2"
              flex={1}
              isLoading={isLoadingDeleteGroceryList}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onPress={deleteGroceryList}
              testID={"confirmDeleteGroceryList-button"}
              borderRadius={theme.borderRadii.button.default}
            >
              {t("general.delete")}
            </Button>
          </HStack>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );

  return (
    <ScreenContainer safeAreaMode="top-horizontal">
      {isLoadingGroceryLists || (isSimpleOrganization && isLoadingCreateGroceryListOnBackend) ? (
        <Center flex={1}>
          <Spinner size="lg" color="primary.50" />
        </Center>
      ) : (
        groceryListComponent
      )}
      {datePickerModal}
      {deleteConfirmationDialog}
    </ScreenContainer>
  );
};
export default GroceryListScreen;
